import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../LanguageSelector";
import LOGO from "../../assets/images/CHARGELOGO.png";
import {
  getRegistrationProgressPercent,
  getRegistrationStepProgressPercent,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../helper/constants";
import {
  RegistrationNavbarLogo,
  RegistrationSetupNavbarRoot,
} from "./RegistrationLayout.style";
import {
  announceRegistrationStep,
  markRegistrationNavigatingBack,
  REGISTRATION_NAVBAR_STEP_LABEL_ID,
} from "../../hook/useRegistrationStepFocus";
import { isA11yAnnouncementsEnabled } from "../../helper/a11yAnnouncements";
import { resolveTranslatedText } from "../../helper/utils";
import { RegistrationA11yToggle } from "./RegistrationA11yToggle";

export const getRegistrationNavbarStepLabel = (
  currentStep,
  totalSteps = REGISTRATION_SETUP_TOTAL_STEPS,
  translate
) =>
  translate("NUTRITION_COMMON_LBL_STEP_OF", {
    current: currentStep,
    total: totalSteps,
  });

export const RegistrationSetupNavbar = ({
  variant = "step",
  stepMeta,
  centerTitle,
  currentStep = 1,
  totalSteps = REGISTRATION_SETUP_TOTAL_STEPS,
  progressPercent,
  onBack,
  showBack = true,
  showProgress = true,
}) => {
  const { t } = useTranslation();
  const isWelcome = variant === "welcome";
  const resolveNavbarTitle = () => {
    if (centerTitle) {
      return centerTitle;
    }
    if (!stepMeta?.navbarTitleKey) {
      return stepMeta?.navbarTitle ?? "";
    }
    const translated = t(stepMeta.navbarTitleKey);
    return translated === stepMeta.navbarTitleKey
      ? stepMeta.navbarTitle ?? translated
      : translated;
  };
  const resolvedCenterTitle = resolveNavbarTitle();
  const resolvedStep = stepMeta?.step ?? currentStep;
  const resolvedProgress =
    progressPercent ??
    (stepMeta
      ? getRegistrationStepProgressPercent(stepMeta, totalSteps)
      : getRegistrationProgressPercent(resolvedStep, totalSteps));
  const stepLabel = getRegistrationNavbarStepLabel(
    resolvedStep,
    totalSteps,
    t
  );
  const showBackButton = !isWelcome && showBack && Boolean(onBack);
  const showStepLabel = showProgress;

  useEffect(() => {
    if (!showStepLabel || !stepLabel || !isA11yAnnouncementsEnabled()) {
      return undefined;
    }

    const pageContext = resolvedCenterTitle
      ? `${stepLabel}. ${resolvedCenterTitle}`
      : stepLabel;

    const timeoutId = window.setTimeout(() => {
      announceRegistrationStep(pageContext);
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showStepLabel, stepLabel, resolvedCenterTitle]);

  return (
    <>
      <RegistrationSetupNavbarRoot className="registration-layout__navbar">
        <div className="registration-layout__navbar-row">
          <div className="registration-layout__navbar-left">
            {showBackButton || showStepLabel ? (
              <div className="registration-layout__navbar-step">
                {showBackButton ? (
                  <button
                    type="button"
                    className="registration-layout__navbar-back-btn"
                    onClick={() => {
                      markRegistrationNavigatingBack();
                      onBack();
                    }}
                    aria-label={
                      showStepLabel
                        ? `${t("GO_BACK_BUTTON")}. ${stepLabel}`
                        : t("GO_BACK_BUTTON")
                    }
                  >
                    ←
                  </button>
                ) : null}
                {showStepLabel ? (
                  <span
                    id={REGISTRATION_NAVBAR_STEP_LABEL_ID}
                    className="registration-layout__navbar-step-label"
                    aria-current="step"
                  >
                    {stepLabel}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div
            className={`registration-layout__navbar-center${
              isWelcome ? " registration-layout__navbar-center--welcome" : ""
            }`}
          >
            {resolvedCenterTitle}
          </div>

          <div className="registration-layout__navbar-right">
            <RegistrationA11yToggle />
            <LanguageSelector variant="light" />
            <RegistrationNavbarLogo
              src={LOGO}
              alt={resolveTranslatedText(
                t("NUTRITION_COMMON_LBL_LOGO_ALT", {
                  defaultValue: "Husk Nutrition logo",
                }),
                "NUTRITION_COMMON_LBL_LOGO_ALT",
                "Husk Nutrition logo"
              )}
            />
          </div>
        </div>
      </RegistrationSetupNavbarRoot>

      {showProgress && (
        <div
          className="registration-layout__navbar-progress"
          role="progressbar"
          aria-labelledby={REGISTRATION_NAVBAR_STEP_LABEL_ID}
          aria-valuenow={resolvedStep}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuetext={stepLabel}
        >
          <div
            className="registration-layout__navbar-progress-fill"
            style={{ width: `${resolvedProgress}%` }}
          />
        </div>
      )}
    </>
  );
};
